/**
 * The project's milestones.
 *
 * A milestone is a date with a name on it, so the list is deliberately thin:
 * tick it, read when it starts and when it is due, see how much of the work
 * filed under it is finished, put it in the right place, delete it. Anything
 * more belongs on a task.
 *
 * Order is the one thing here that is not derived. `sortOrder` is stored, the
 * API sorts on it before the due date, and until the up/down buttons below there
 * was no writer for it anywhere in the app - so every row in every project sits
 * at 0 and the list is really sorted by date. That is why a move renumbers the
 * WHOLE list rather than swapping two values: swapping 0 with 0 changes nothing,
 * and the operator would watch the row jump back on the next reload.
 *
 * ## The task counts come from the summary, and their ABSENCE is an answer
 *
 * `summary.milestones` is a per-milestone task rollup on its own fetch, joined
 * here by id rather than folded into the milestone rows. The join has one rule,
 * and this file is the easiest place in the app to break it:
 *
 * - an id PRESENT in the rollup gets its count drawn, 0 / 0 included. 0 / 0 is a
 *   real statement: nothing has been filed under this phase yet.
 * - an id ABSENT gets NOTHING drawn. Absent means the summary has not landed,
 *   or the operator added this milestone a second ago and the reload it fired is
 *   still in flight - a brand new milestone is missing from the rollup until the
 *   summary catches up. A 0 / 0 in that gap is a figure with nobody behind it,
 *   and the operator cannot tell it apart from the true one.
 *
 * `onChange` is the summary reload, not a project reload, which is also why the
 * list below is kept locally and only seeded from the prop: a project refetch
 * would drop a fresh array over the operator's half-finished reordering.
 *
 * ## Two views of the same phases
 *
 * The list answers WHEN a phase runs and how much of it is ticked; the board
 * (`MilestoneBoard.tsx`) answers WHAT is in one. The pills in the toolbar choose
 * between them, and the quick add stays on screen in both - a phase typed while
 * the board is up appears as an empty column, which is the only thing a card can
 * be moved onto.
 *
 * The choice is LOCAL STATE and is not written to the address bar. The tab
 * already lives in `?tab=milestones`, and a second parameter beside it would be
 * a second thing every link to this project has to carry correctly; the list is
 * the one the tab has always opened on, so a link somebody sent last month keeps
 * landing on what they were describing.
 *
 * `revision` exists for the ONE control the two views share: the quick add in
 * the toolbar. A phase typed while the board is up has to appear as a column,
 * and the board holds its own fetch - one request carrying the phases, their
 * tasks and their tallies - so nothing here can patch its copy without
 * describing the whole grouping a second time. Bumping the counter remounts it
 * instead, which costs one request and cannot go stale; it is safe because the
 * board holds no operator input to throw away.
 *
 * The renames, the reordering, the tick and the delete need no such signal, and
 * that is worth stating rather than leaving to be rediscovered: every one of
 * them is a control of the LIST, and reaching the list unmounts the board - so
 * coming back to it is a fresh request already. A control moved out of the list
 * and into the shared toolbar would have to bump the counter like the add does.
 */
import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock, CheckCircle2, ChevronDown, ChevronUp, Circle, Flag, Loader2, MoreHorizontal, Pencil, Plus, Trash2,
} from "lucide-react";
import { cn, DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import {
  addMilestone, deleteMilestone, updateMilestone,
  type Milestone as MilestoneRow, type Project, type ProjectSummary,
} from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { Btn, EmptyState, PillTabs, StatusPill, Toolbar, ToolbarGap, fmtDate, useToast, listCard } from "../../ui";
import { MilestoneBoard } from "./MilestoneBoard";
import { MilestoneEditModal } from "./MilestoneEditModal";
import { useMilestoneToggle } from "./useMilestoneToggle";
import { toIso } from "./lib";

/** Name, state, start, due date, task rollup, order, actions. */
const COLS = "minmax(0,2.2fr) 1fr .9fr .9fr .85fr 76px 44px";

/**
 * The two shapes this tab draws.
 *
 * The array is the one place that says which values are legal and a `Record`
 * gives each one its word, the same pair `tasks/TasksToolbar.tsx` uses: a third
 * view added here without a label is a type error rather than a pill with
 * nothing written on it. The wording is borrowed from the task register on
 * purpose - "List" and "Board" already mean these two arrangements everywhere
 * else in the CRM, and a third pair of synonyms would only invite the question
 * of how they differ.
 */
const VIEWS = ["list", "board"] as const;
type View = (typeof VIEWS)[number];
const VIEW_LABEL: Record<View, string> = {
  list: "tasks.viewList",
  board: "tasks.viewBoard",
};

/**
 * A milestone that ends before it begins.
 *
 * Both dates or nothing: a row with only one of them cannot contradict itself,
 * and treating a missing start as "today" would invent the contradiction. Local
 * to this file rather than in `lib.ts` because this is the only caller - the
 * edit dialog stops the pair from being made at all instead of judging it after
 * the fact, so it never has to ask.
 */
function startsAfterDue(m: MilestoneRow): boolean {
  return !!m.startDate && !!m.dueDate && new Date(m.startDate) > new Date(m.dueDate);
}

/**
 * The date field in the toolbar's dress rather than the form's.
 *
 * `index.css` gives every `DatePicker` the sunken 52px field, which is right in
 * a dialog and wrong in this row: here the picker sits between a raised input
 * and a button, so it asks for `crm-fld-date` - the same raised 44px pill the
 * register's date-range filter wears. Only the width is local.
 */
const DATE_PILL = "crm-fld-date w-44";

function RowMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const t = useT();
  const close = useClosePops();
  return (
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onEdit();
          }}
        >
          <Pencil className="size-4" />
          {t("projects.milestoneEdit")}
        </button>
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>
    </span>
  );
}

export function Milestones({
  project,
  summary,
  onChange,
}: {
  project: Project & { milestones: MilestoneRow[] };
  summary: ProjectSummary | null;
  onChange: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const { locale } = useI18n();
  const [items, setItems] = useState<MilestoneRow[]>(project.milestones);
  const [name, setName] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [editing, setEditing] = useState<MilestoneRow | null>(null);
  const [view, setView] = useState<View>("list");
  /** Bumped by every write in this file, so the board refetches. See the header. */
  const [revision, setRevision] = useState(0);
  const toggle = useMilestoneToggle(setItems, onChange);

  /**
   * A phase was added from the toolbar, which both views share.
   *
   * Kept beside `onChange` rather than folded into it: `onChange` is the project
   * page's summary reload and belongs to the caller, while this is this tab
   * telling its own board that its columns are out of date. Separate, so a
   * caller who passes a different `onChange` cannot quietly stop the board from
   * noticing.
   */
  const bump = () => setRevision((n) => n + 1);

  useEffect(() => {
    setItems(project.milestones);
  }, [project.milestones]);

  // A Map rather than a `find` per row, and built from the summary alone: a
  // milestone the rollup does not mention has to come back undefined, so the
  // lookup itself carries the "we do not know yet" case to the cell below.
  const rollup = useMemo(
    () => new Map((summary?.milestones ?? []).map((m) => [m.id, m])),
    [summary],
  );

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const m = await addMilestone(project.id, { name: name.trim(), dueDate: toIso(due) ?? null });
      setItems((prev) => [...prev, m]);
      setName("");
      setDue("");
      onChange();
      bump();
    } finally {
      setBusy(false);
    }
  }

  /**
   * Move one row one place, and renumber everything the move displaced.
   *
   * Sent as ONE volley rather than a loop of awaited PATCHes. A list of ten
   * milestones is nine writes on the first ever move (every row but the top one
   * changes number), and nine sequential round trips is a visibly frozen list -
   * worse, an error halfway through a sequence leaves the order half written,
   * with no way to say which half. Fired together, the failure is one failure:
   * the list goes back to what it looked like and `onChange` refetches, so what
   * the operator ends up looking at is what the server actually holds rather
   * than this component's guess about it.
   */
  async function reorder(index: number, dir: -1 | 1) {
    const to = index + dir;
    if (ordering || to < 0 || to >= items.length) return;

    const before = items;
    const next = before.slice();
    const [row] = next.splice(index, 1);
    if (!row) return;
    next.splice(to, 0, row);

    const numbered = next.map((m, i) => ({ ...m, sortOrder: i }));
    const wasOrder = new Map(before.map((m) => [m.id, m.sortOrder]));
    const changed = numbered.filter((m) => wasOrder.get(m.id) !== m.sortOrder);
    if (changed.length === 0) return;

    setItems(numbered);
    setOrdering(true);
    try {
      await Promise.all(changed.map((m) => updateMilestone(m.id, { sortOrder: m.sortOrder })));
      onChange();
    } catch {
      setItems(before);
      toast(t("common.error"));
      // A rejected volley does not mean nothing landed: the other writes were
      // already in flight. Ask the server what the order is now.
      onChange();
    } finally {
      setOrdering(false);
    }
  }

  async function remove(id: string) {
    try {
      await deleteMilestone(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      onChange();
    } catch {
      toast(t("common.error"));
    }
  }

  return (
    <>
      {/* Two fields, not three. The quick-add answers the question that made the
          operator reach for it - what is this phase called and when is it owed -
          and a start date is a correction to a plan, which has a dialog one
          click away in the row menu. It is also the field whose validity depends
          on ANOTHER field: in here the deadline may still be empty, so a start
          typed beside it can be bounded by nothing, and the inverted pair the
          edit dialog refuses to make would be born in the add form instead. */}
      <Toolbar>
        <form onSubmit={add} className="flex flex-1 flex-wrap items-center gap-2.5">
          <label className="crm-fld min-w-52 flex-1">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects.milestoneName")}
              autoComplete="off"
            />
          </label>
          <DatePicker
            value={due}
            onChange={setDue}
            locale={locale}
            placeholder={t("projects.deadline")}
            className={DATE_PILL}
          />
          <Btn type="submit" tone="dark" disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t("common.add")}
          </Btn>
        </form>

        {/* The pills sit at the right edge, where every other view switch in the
            CRM sits. The add form keeps its `flex-1`, so the gap is what stops a
            long field from pushing them off the row on a narrow window. */}
        <ToolbarGap />
        <PillTabs
          value={view}
          onChange={setView}
          tabs={VIEWS.map((v) => ({ value: v, label: t(VIEW_LABEL[v]) }))}
        />
      </Toolbar>

      {view === "board" ? (
        // Keyed on the revision so a phase added from the shared toolbar becomes
        // a column. See the header.
        <MilestoneBoard key={revision} projectId={project.id} onChange={onChange} />
      ) : (
        <div className="crm-card" style={listCard(COLS)}>
          {items.length === 0 ? (
            <EmptyState icon={Flag} title={t("projects.noMilestones")} />
          ) : (
            <>
              <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
                <span>{t("projects.milestoneName")}</span>
                <span>{t("projects.statusCol")}</span>
                <span>{t("projects.milestoneStart")}</span>
                <span>{t("projects.deadline")}</span>
                <span>{t("projects.tasks")}</span>
                <span />
                <span />
              </div>

              <div className="crm-rows pt-0">
                {items.map((m, i) => {
                  const late = !m.done && !!m.dueDate && new Date(m.dueDate) < new Date();
                  const backwards = startsAfterDue(m);
                  const count = rollup.get(m.id);
                  return (
                    <div key={m.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
                      <span className="flex min-w-0 items-center gap-2.5">
                        <button
                          type="button"
                          className="crm-cb crm-cb-sm shrink-0"
                          onClick={() => toggle(m)}
                          title={t("projects.roadmap.completed")}
                          aria-label={t("projects.roadmap.completed")}
                          aria-pressed={m.done}
                        >
                          {m.done ? (
                            <CheckCircle2 className="size-4 text-[var(--color-success)]" />
                          ) : (
                            <Circle className="size-4" />
                          )}
                        </button>
                        <span className={cn("crm-prow-n crm-trunc", m.done && "crm-mut line-through")}>{m.name}</span>
                      </span>

                      <span>
                        {/* Three states, not two: done, still ahead, and gone by.
                            A late milestone shown as "planned" is the one thing
                            this column exists to stop. */}
                        <StatusPill tone={m.done ? "ok" : late ? "lt" : "wt"}>
                          {t(
                            m.done
                              ? "projects.roadmap.completed"
                              : late
                                ? "projects.overdue"
                                : "projects.roadmap.planned",
                          )}
                        </StatusPill>
                      </span>

                      {/* No calendar glyph on this one. Two identical marks on one
                          row read as a repeated control rather than two columns,
                          and the deadline is the date that can go wrong by itself.

                          The red is the one thing the edit dialog cannot say for
                          rows it did not write. That dialog bounds each picker by
                          the other, so an operator can no longer PRODUCE a start
                          that falls after its own deadline - but the importer and
                          the API can, and 13 of the 13 milestones in production
                          arrived from an importer. Left in the muted tone, the
                          only rows carrying the error would be the rows nobody
                          looks twice at. The colour is a flag, not a sentence:
                          the sentence needs a string the catalogue does not have
                          yet (see the note handed back with this change). */}
                      <span className={cn("crm-tnum text-[12.5px]", backwards ? "text-red-600" : "crm-mut")}>
                        {fmtDate(m.startDate)}
                      </span>

                      <span
                        className={cn("crm-tnum flex items-center gap-1.5 text-[12.5px]", late ? "text-red-600" : "crm-mut")}
                      >
                        <CalendarClock className="size-3.5 shrink-0" />
                        {fmtDate(m.dueDate)}
                      </span>

                      {/* Empty when the rollup has not spoken about this id. See
                          the join rule at the top of the file: this cell prints
                          0 / 0 gladly, and prints nothing at all rather than
                          guess. */}
                      <span className="crm-mut crm-tnum text-[12.5px]">
                        {count ? t("projects.milestoneTasks", { done: count.done, total: count.total }) : null}
                      </span>

                      <span className="flex items-center gap-1.5">
                        <button
                          type="button"
                          className="crm-cb crm-cb-sm"
                          onClick={() => void reorder(i, -1)}
                          disabled={ordering || i === 0}
                          title={t("projects.milestoneUp")}
                          aria-label={t("projects.milestoneUp")}
                        >
                          <ChevronUp className="size-4" />
                        </button>
                        <button
                          type="button"
                          className="crm-cb crm-cb-sm"
                          onClick={() => void reorder(i, 1)}
                          disabled={ordering || i === items.length - 1}
                          title={t("projects.milestoneDown")}
                          aria-label={t("projects.milestoneDown")}
                        >
                          <ChevronDown className="size-4" />
                        </button>
                      </span>

                      <RowMenu onEdit={() => setEditing(m)} onDelete={() => void remove(m.id)} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {editing ? (
        <MilestoneEditModal
          milestone={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setItems((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
            onChange();
          }}
        />
      ) : null}
    </>
  );
}
